import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { ItemsList } from '@/components/ItemsList';
import { ChangeHistory } from '@/components/ChangeHistory';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { ErrorMessage } from '@/components/ErrorMessage';
import { api, APIError } from '@/api/client';
import type { SOW } from '@/types';

export function ClientSOWView() {
  const { token } = useParams<{ token: string }>();
  const [sow, setSOW] = useState<SOW | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [acknowledgeError, setAcknowledgeError] = useState('');

  const loadSOW = useCallback(() => {
    if (!token) return;

    setLoading(true);
    api
      .getClientSOW(token)
      .then((response) => {
        setSOW(response.data);
        setLoading(false);
      })
      .catch((err) => {
        if (err instanceof APIError) {
          setError(err.message);
        } else {
          setError('Failed to load SOW');
        }
        setLoading(false);
      });
  }, [token]);

  useEffect(() => {
    loadSOW();
  }, [loadSOW]);

  const handleAcknowledge = async (changeId: string) => {
    if (!token) return;

    setAcknowledgeError('');
    try {
      await api.acknowledgeChange(token, changeId);
      loadSOW();
    } catch (err) {
      if (err instanceof APIError) {
        setAcknowledgeError(`Failed to acknowledge: ${err.message}`);
      } else {
        setAcknowledgeError('Failed to acknowledge change');
      }
    }
  };

  if (loading) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout>
        <ErrorMessage message={error} />
      </Layout>
    );
  }

  if (!sow) {
    return (
      <Layout>
        <ErrorMessage message="SOW not found" />
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-5xl mx-auto space-y-8">
        {acknowledgeError && <ErrorMessage message={acknowledgeError} />}

        <div>
          <h1 className="text-3xl font-bold text-gray-900">
            Statement of Work
          </h1>
          <p className="text-gray-600 mt-2">
            Created: {new Date(sow.createdAt).toLocaleDateString()}
          </p>
          <p className="text-sm text-gray-500 mt-1">Read-only client view</p>
        </div>

        <div>
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            Current Scope
          </h2>
          <ItemsList items={sow.items} />
        </div>

        {sow.changes && sow.changes.length > 0 && (
          <ChangeHistory
            changes={sow.changes}
            onAcknowledge={handleAcknowledge}
            showAcknowledgeButton={true}
          />
        )}
      </div>
    </Layout>
  );
}
